import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { Autocomplete, Box, CircularProgress, Divider, TextField, Typography } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import VouchersAPI from 'api/faber/VouchersAPI'
import { BootstrapDialog, BootstrapDialogTitle } from 'common/AbstractModal/BootstrapDialog'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'

interface ServiceVoucher {
  name: string;
  type: string;
}

const serviceVouchers: ServiceVoucher[] = [
  { name: 'Fast Track', type: 'FAST_TRACK' },
  { name: 'Lounge Pass', type: 'LOUNGE_PASS' }
]

interface AssignVoucherDialogProps {
  open: boolean
  userId: string
  onClose: () => void
}

export default function AssignVoucherDialog({
  userId,
  open,
  onClose
}: AssignVoucherDialogProps) {
  const queryClient = useQueryClient();
  const [voucherType, setVoucherType] = useState('');
  const faberToken = OrbitSession.get('faberToken');

  const { mutate, isLoading } = useMutation(VouchersAPI.assignCustomerServiceVoucher, {
    onSuccess: ({data}) => {
      const voucherCode = data?.data?.code;
      toast.success(`Voucher assigned successfully. Voucher code: ${voucherCode}`)
      queryClient.refetchQueries({ queryKey: ['userVoucherData'] })
    },
    onError: () => {
      toast.error('Failed to assign customer voucher')
    },
    onSettled: () => {
      onClose()
      setVoucherType('');
    }
  });

  const handleAssign = () => {
    if (voucherType == "") {
      toast.error("Please select a voucher.");
      return
    }
    
    mutate({
      faberToken,
      userId,
      voucherType
    });
  }

  return (
    <div>
      <BootstrapDialog
        fullWidth
        onClose={onClose}
        aria-labelledby='customized-dialog-title'
        open={open}
      >
        <BootstrapDialogTitle onClose={onClose}>
          Assign Customer Voucher
        </BootstrapDialogTitle>

        <Divider sx={{ width: '100%' }} />

        <DialogContent>
          <NormalText sx={{ p: 0 }}>
            You are about to assign a voucher to this customer. This action will grant the selected voucher to the user with ID: <b>{userId}</b>.
          </NormalText>
        </DialogContent>

        <Box sx={{ paddingX: 3, mb: 3 }}>
          <Typography sx={{ minWidth: '100px', mb: 1 }}>
            Choose a voucher to assign to this customer
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            options={serviceVouchers}
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => setVoucherType(newValue?.type || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select voucher"
                variant="outlined"
              />
            )}
          />
        </Box>

        <DialogActions sx={{ mb: '1rem' }}>
          <RoundedButton onClick={onClose}>
            Cancel
          </RoundedButton>
          <LoadingButton
            loading={isLoading}
            variant='filled'
            onClick={handleAssign}
            loadingIndicator={<CircularProgress color='inherit' size={16} />}
            autoFocus
          >
            Assign
          </LoadingButton>
        </DialogActions>
      </BootstrapDialog>
    </div>
  )
}
